import { useState, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { maskedChange, maskPhone } from '@/lib/mask';

/**
 * Поле ввода.
 *
 * Лейбл **всегда над полем**; плейсхолдер вместо лейбла запрещён (кит).
 * Ширину задаёт контейнер, а не поле: в колонке `.form` оно растянется
 * на всю колонку, в ряду `.row` займёт долю. Своих ширин у компонента нет.
 */

interface FieldShellProps {
  label: string;
  /**
   * Подпись под полем. В ките для этого нет отдельного класса —
   * берётся `.sub`, вторичный текст. Макет в паре мест пишет
   * `class="hint"`, которого в CSS нет вовсе; повторять эту опечатку
   * незачем.
   */
  hint?: ReactNode;
  /** Ошибка под полем — объясняет, как исправить */
  error?: string | null;
  children: ReactNode;
}

function FieldShell({ label, hint, error, children }: FieldShellProps) {
  return (
    <label className="field">
      <span className="lbl">{label}</span>
      {children}
      {error ? <span className="err">{error}</span> : hint ? <span className="sub">{hint}</span> : null}
    </label>
  );
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  onValue?: (value: string) => void;
}

export function TextField({ label, hint, error, onValue, ...rest }: TextFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error}>
      <input {...rest} onChange={(e) => onValue?.(e.target.value)} />
    </FieldShell>
  );
}

interface PhoneFieldProps extends Omit<TextFieldProps, 'onValue' | 'value' | 'inputMode'> {
  value: string;
  onValue: (value: string) => void;
}

/**
 * Телефон с маской `+7 (___) ___-__-__` (доработка M2-1 п. 2): 8 или 7
 * в начале — код страны, номер с «+» и другим кодом вводится как есть.
 * Наружу — строка как в поле; к единому виду номер приводит сервер.
 */
export function PhoneField({ label, hint, error, value, onValue, placeholder, ...rest }: PhoneFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error}>
      <input
        {...rest}
        inputMode="tel"
        autoComplete="tel"
        placeholder={placeholder ?? '+7 (___) ___-__-__'}
        value={value}
        onChange={(e) => onValue(maskedChange(value, e.target.value, e.target.selectionStart, maskPhone))}
      />
    </FieldShell>
  );
}

interface NumberFieldProps extends Omit<TextFieldProps, 'onValue' | 'value'> {
  value: number | null;
  /** Каждое изменение — для формы, которая сохраняется кнопкой */
  onValue?: (value: number | null) => void;
  /**
   * Сохранение сразу в карточке: один раз, когда ввод закончен — уход
   * из поля или Enter, — как у текстовых полей карточки. Пока идёт ввод,
   * поле показывает набранное, а не значение с сервера: иначе ответ
   * на промежуточную цифру откатывал бы поле, следующие цифры дописывались
   * к старому значению, а «5» на пути к «5000» уходило бы в базу ценой.
   * Вернул промис, и он отклонён — поле возвращается к значению с сервера.
   */
  onCommit?: (value: number | null) => unknown;
}

const digitsOf = (text: string) => text.replace(/[^\d]/g, '');

/**
 * Числовое поле. Цифры табличные — за это отвечает CSS кита;
 * здесь только то, что пустое поле означает «не задано», а не ноль.
 */
export function NumberField({ label, hint, error, value, onValue, onCommit, ...rest }: NumberFieldProps) {
  // Набранное, пока не сохранено; null — показываем значение с сервера
  const [draft, setDraft] = useState<string | null>(null);
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setDraft(null);
  }
  const shown = draft ?? (value === null ? '' : String(value));

  const commit = () => {
    if (!onCommit || draft === null) return;
    const next = draft === '' ? null : Number(draft);
    if (next === value) return setDraft(null);
    const result = onCommit(next);
    if (result instanceof Promise) result.catch(() => setDraft(null));
    else setDraft(null);
  };

  return (
    <FieldShell label={label} hint={hint} error={error}>
      <input
        inputMode="numeric"
        {...rest}
        value={shown}
        onChange={(e) => {
          const digits = digitsOf(e.target.value);
          if (onCommit) setDraft(digits);
          else onValue?.(digits === '' ? null : Number(digits));
        }}
        onBlur={(e) => {
          commit();
          rest.onBlur?.(e);
        }}
        onKeyDown={(e) => {
          if (onCommit && e.key === 'Enter') e.currentTarget.blur();
          rest.onKeyDown?.(e);
        }}
      />
    </FieldShell>
  );
}

interface TextAreaFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  onValue?: (value: string) => void;
}

export function TextAreaField({ label, hint, error, onValue, ...rest }: TextAreaFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error}>
      <textarea rows={3} {...rest} onChange={(e) => onValue?.(e.target.value)} />
    </FieldShell>
  );
}

/**
 * Ряд полей: доли, а не пиксели (кит, п. 13). `fix` — узкое по смыслу
 * поле: время, количество. Ширину такому полю задаёт не style, а `size`
 * у input — иначе в разметке появляются числа, которых там быть не должно.
 */
export function Row({ children }: { children: ReactNode }) {
  return <div className="row">{children}</div>;
}

export function Col({ fix, children }: { fix?: boolean; children: ReactNode }) {
  return <div className={fix ? 'col fix' : 'col'}>{children}</div>;
}

export function Form({ children }: { children: ReactNode }) {
  return <div className="form">{children}</div>;
}
